import { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import { Container } from '@mui/material';
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import ContactForm from '../components/form/contact_form.tsx';
import './contatti.scss';

gsap.registerPlugin(ScrollTrigger);

function Contatti() {
  const heroTitleRef = useRef<HTMLHeadingElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    // Animazione titolo hero
    if (heroTitleRef.current) {
      const lines = heroTitleRef.current.querySelectorAll('.hero-line');
      gsap.fromTo(lines,
        { y: 100, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1.2,
          stagger: 0.15,
          ease: "power3.out",
          delay: 0.3
        }
      );
    }

    // Parallasse video
    const handleScroll = () => {
      if (videoRef.current) {
        const scrolled = window.pageYOffset;
        videoRef.current.style.transform = `translateY(${scrolled * 0.5}px) scale(1.1)`;
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Animazione cards
    const cards = document.querySelectorAll('.contact-card');
    cards.forEach((card) => {
      gsap.fromTo(card,
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: "power2.out",
          scrollTrigger: {
            trigger: card,
            start: "top 85%",
            toggleActions: "play none none none"
          }
        }
      );
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Contatti | Richiedi un Preventivo Sito Web Personalizzato - Camilla Cino</title>
        <meta name="description" content="Hai bisogno di un sito web professionale o di un restyling? Contattami per un preventivo gratuito. Web designer freelance con base nel Nord Italia (Modena, Bologna, Milano)." />
        <meta name="keywords" content="preventivo sito web, contatti web designer, consulenza web design, richiesta preventivo, web designer Modena, web designer freelance contatti" />
        <meta property="og:title" content="Contatti | Richiedi un Preventivo Sito Web Personalizzato" />
        <meta property="og:description" content="Hai bisogno di un sito web professionale o restyling? Contattami per un preventivo gratuito." />
        <meta property="og:image" content="https://www.camillacinodesigndev.it/loghi/sito/preview.png" />
        <meta property="og:url" content="https://www.camillacinodesigndev.it/contatti" />
        <meta property="og:locale" content="it_IT" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contatti | Preventivo Sito Web" />
        <meta name="twitter:description" content="Contattami per un preventivo gratuito. Web designer freelance Nord Italia." />
        <link rel="canonical" href="https://www.camillacinodesigndev.it/contatti" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "Contatti Web Designer Freelance",
            "description": "Richiedi un preventivo gratuito per siti web professionali",
            "url": "https://www.camillacinodesigndev.it/contatti",
            "mainEntity": {
              "@type": "ProfessionalService",
              "name": "Camilla Cino Design & Dev",
              "email": "info@camillacinodesigndev.it",
              "telephone": "+39-3482659545",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Modena",
                "addressRegion": "Emilia-Romagna",
                "addressCountry": "IT"
              }
            }
          })}
        </script>
      </Helmet>

      <div className="contatti-page">
        {/* Hero Section */}
        <section className="contatti-hero">
          <video
            ref={videoRef}
            className="hero-video"
            autoPlay
            muted
            loop
            playsInline
            suppressHydrationWarning
          >
            <source src="/video/video2.mp4" type="video/mp4" />
          </video>
          <div className="hero-overlay"></div>
          <div className="hero-bg"></div>
          <Container maxWidth="xl" className="hero-content">
            <div className="hero-grid">
              <div className="hero-text">
                <div className="hero-label">
                  <span className="label-line"></span>
                  Parliamone
                </div>

                <h1 className="hero-title" ref={heroTitleRef}>
                  <span className="hero-line">Rimaniamo in</span>
                  <span className="hero-line"><span className="gradient-text">Contatto</span></span>
                </h1>

                <p className="hero-description">
                  Hai un progetto in mente? Vuoi un preventivo gratuito?
                  Scrivimi e ti risponderò entro 24 ore.
                </p>

                <button
                  className="hero-cta"
                  onClick={() => {
                    document.getElementById('form')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Scrivimi
                  <span className="arrow">↓</span>
                </button>
              </div>

              <div className="hero-preview">
                <img src="/contatti/contatti.png" alt="Contattami" className="hero-illustration" />
              </div>
            </div>
          </Container>
        </section>

        {/* Recapiti Section */}
        <section className="form-section" id="form">
          <Container maxWidth="xl">
            <div className="section-header">
              <h2 className="section-title">
                <span className="number">01</span>
                Recapiti
              </h2>
            </div>

            <ContactForm />
          </Container>
        </section>

        {/* Info Section */}
        <section className="info-section" style={{ backgroundImage: "url('/sfondi/sfondo2.png')" }}>
          <Container maxWidth="xl">
            <div className="section-header centered">
              <h2 className="section-title">
                <span className="number">02</span>
                Come Lavoro
              </h2>
            </div>

            <div className="info-grid">
              <div className="contact-card">
                <span className="card-number">01</span>
                <h3 className="card-title">Contatto</h3>
                <p className="card-description">
                  Mi scrivi, ti rispondo entro 24 ore per fissare una call conoscitiva gratuita.
                </p>
              </div>
              <div className="contact-card">
                <span className="card-number">02</span>
                <h3 className="card-title">Analisi</h3>
                <p className="card-description">
                  Ascolto le tue esigenze, analizzo il progetto e preparo un preventivo dettagliato.
                </p>
              </div>
              <div className="contact-card">
                <span className="card-number">03</span>
                <h3 className="card-title">Sviluppo</h3>
                <p className="card-description">
                  Lavoriamo insieme per creare il tuo sito, con aggiornamenti costanti sul progresso.
                </p>
              </div>
            </div>
          </Container>
        </section>
      </div>
    </>
  );
}

export default Contatti;
